import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("staff");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchMembers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchMembers = async () => {
    const { data } = await supabase
      .from("team_members")
      .select("*")
      .order("created_at", { ascending: false });
    setMembers(data || []);
    setLoading(false);
  };

  const addMember = async () => {
    if (!newEmail || !newName) {
      setError("Please fill in name and email");
      return;
    }
    setAdding(true);
    setError("");

    const { error: dbError } = await supabase.from("team_members").insert({
      email: newEmail,
      name: newName,
      role: newRole,
    });

    if (dbError) {
      setError(dbError.message);
      setAdding(false);
      return;
    }

    setSuccess(
      `${newName} added! Now go to Supabase → Authentication → Invite user → type ${newEmail} to send their login link.`
    );
    setNewEmail("");
    setNewName("");
    setNewRole("staff");
    setShowAdd(false);
    setAdding(false);
    fetchMembers();
  };

  const removeMember = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    await supabase.from("team_members").delete().eq("id", id);
    fetchMembers();
  };

  const getRoleBadge = (role) =>
    role === "admin"
      ? { background: "#eaf3de", color: "#3b6d11" }
      : { background: "#f0f0f0", color: "#666" };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const inputStyle = {
    width: "100%",
    border: "0.5px solid #e0e0e0",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
    color: "#111",
  };

  const btnStyle = (bg, color, opacity = 1) => ({
    background: bg,
    color,
    fontSize: "12px",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    padding: "7px 14px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    opacity,
  });

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}

      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile && <MobileHeader title="Team" />}

        {/* Top bar */}
        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="t-page-title">Team</div>
          <button
            onClick={() => {
              setShowAdd(true);
              setError("");
              setSuccess("");
            }}
            style={btnStyle("#3a9e5f", "#fff")}
          >
            + Add member
          </button>
        </div>

        <div
          style={{
            padding: isMobile ? "12px" : "20px 24px",
            maxWidth: "700px",
          }}
        >
          {/* Success message */}
          {success && (
            <div
              style={{
                background: "#eaf3de",
                border: "0.5px solid #3a9e5f",
                borderRadius: "8px",
                padding: "10px 14px",
                marginBottom: "16px",
              }}
            >
              <span className="t-body" style={{ color: "#3b6d11" }}>
                ✅ {success}
              </span>
            </div>
          )}

          {/* Add member form */}
          {showAdd && (
            <div
              style={{
                background: "#fff",
                border: "0.5px solid #e8e8e8",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "16px",
              }}
            >
              <div className="t-name" style={{ marginBottom: "16px" }}>
                Add new team member
              </div>

              {error && (
                <div
                  style={{
                    background: "#fcebeb",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    marginBottom: "12px",
                  }}
                >
                  <span className="t-body" style={{ color: "#a32d2d" }}>
                    {error}
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "12px",
                  marginBottom: "14px",
                }}
              >
                <div>
                  <div className="t-label" style={{ marginBottom: "4px" }}>
                    Full name
                  </div>
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="John Smith"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <div className="t-label" style={{ marginBottom: "4px" }}>
                    Email
                  </div>
                  <input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="john@porsofreight.com"
                    type="email"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <div className="t-label" style={{ marginBottom: "4px" }}>
                    Role
                  </div>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    style={{ ...inputStyle, background: "#fff" }}
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={addMember}
                  disabled={adding}
                  style={btnStyle("#3a9e5f", "#fff", adding ? 0.6 : 1)}
                >
                  {adding ? "Adding..." : "Add member"}
                </button>
                <button
                  onClick={() => setShowAdd(false)}
                  style={btnStyle("#f0f0f0", "#555")}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Team members list */}
          <div
            style={{
              background: "#fff",
              border: "0.5px solid #e8e8e8",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {/* Header row */}
            <div
              style={{
                padding: "10px 16px",
                borderBottom: "0.5px solid #e8e8e8",
                background: "#fafafa",
              }}
            >
              <div className="t-table-header">
                {members.length} member{members.length !== 1 ? "s" : ""}
              </div>
            </div>

            {loading ? (
              <div
                style={{ padding: "40px", textAlign: "center" }}
                className="t-body"
              >
                Loading...
              </div>
            ) : members.length === 0 ? (
              <div
                style={{ padding: "40px", textAlign: "center" }}
                className="t-body"
              >
                No team members yet
              </div>
            ) : (
              members.map((member) => (
                <div
                  key={member.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    borderBottom: "0.5px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {/* Avatar */}
                    <div
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        background: "#eaf3de",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#3a9e5f",
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(member.name)}
                    </div>
                    <div>
                      {/* Name */}
                      <div className="t-name">{member.name}</div>
                      {/* Email — DM Mono */}
                      <div className="t-data" style={{ color: "#9ca3af" }}>
                        {member.email}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {/* Role badge */}
                    <span
                      className="t-badge"
                      style={{
                        padding: "3px 8px",
                        borderRadius: "20px",
                        ...getRoleBadge(member.role),
                      }}
                    >
                      {member.role}
                    </span>
                    {/* Remove button */}
                    <button
                      onClick={() => removeMember(member.id, member.email)}
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        fontWeight: "400",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#a32d2d")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#9ca3af")
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
